/**
 * TransactionTableHeader - Phase A / Comprehensive header
 * ========================================================
 *
 * Row 1 - KPI strip: Money In · Money Out · Net · Avg · Awaiting · Auto-posted · Tax-flagged
 * Row 2 - Filters: date range · bank · GL · direction · source · tax · confidence · search
 * Row 3 - Bulk action toolbar (only when ≥1 row is selected)
 * Row 4 - Tax summary chips (clickable shortcuts)
 *
 * Density default = comfortable (≥10 rows / page) - owner-friendly per user choice.
 */
import React, { useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import {
  Receipt, Sparkles, Search,
  Filter, X, Table as TableIcon, LayoutGrid, ThumbsUp, Edit3, Scissors,
  Trash2, Link2, ArrowDownRight, ArrowUpRight, Plus,
} from 'lucide-react';
import { formatCurrency } from '../../utils/currency';

const Chip = ({ children, active, onClick, count, tone, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
      active ? (tone || 'border-[#108981] text-[#108981] bg-[#108981]/10 ring-2 ring-[#108981]/30')
             : 'border-border text-muted-foreground bg-background hover:bg-accent'
    }`}
  >
    {children}
    {count > 0 && (
      <span className={`text-[10px] font-bold tabular-nums ${active ? '' : 'text-foreground'}`}>{count}</span>
    )}
  </button>
);

export const TransactionTableHeader = ({
  mode,                         // 'pending' | 'completed'
  transactions = [],            // current visible rows
  currency = 'NGN',
  // selection
  selectedCount = 0,
  totalCount = 0,
  isAllSelected = false,
  onToggleSelectAll,
  onClearSelection,
  // bulk handlers
  onBulkApprove,
  onBulkReclassify,
  onBulkApplyVat,
  onBulkApplyWht,
  onBulkMarkDuplicate,
  onBulkDelete,
  // filters
  filters,                      // { search, direction, bankId, glCode, source, tax, confidence }
  onFiltersChange,
  bankAccounts = [],
  glAccounts = [],
  // view toggle
  viewMode = 'table',           // 'table' | 'cards'
  onViewModeChange,
  showViewToggle = true,        // In Books is table-only
  // tax context
  taxCtx,
  // tax summary chips (counts come from server stats when available)
  vatCandidates = 0,
  whtCandidates = 0,
  potentialInputVat = 0,
}) => {
  // Mobile: filters are collapsed behind a toggle so users reach the actual
  // transactions with far less scrolling (user request, June 2026).
  const [showFilters, setShowFilters] = useState(false);

  const setFilter = (k, v) => onFiltersChange?.({ ...filters, [k]: v });
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters?.search) n++;
    if (filters?.direction && filters.direction !== 'all') n++;
    if (filters?.bankId && filters.bankId !== 'all') n++;
    if (filters?.glCode && filters.glCode !== 'all') n++;
    if (filters?.source && filters.source !== 'all') n++;
    if (filters?.tax && filters.tax !== 'all') n++;
    if (filters?.confidence && filters.confidence !== 'all') n++;
    if (filters?.startDate || filters?.endDate) n++;
    return n;
  }, [filters]);

  return (
    <div className="space-y-3" data-testid={`tx-table-header-${mode}`}>
      {/* KPI strip removed (June 2026) - users found it noise between them
          and the actual transactions on mobile. */}

      {/* Row 2 - Filters bar (collapsible on mobile) */}
      <Card className="p-3 flex flex-wrap items-center gap-2" data-testid="tx-filters-bar">
        <div className="relative flex-1 min-w-[160px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search counterparty, description, reference…"
            value={filters?.search || ''}
            onChange={(e) => setFilter('search', e.target.value)}
            className="pl-8 h-9 text-sm"
            data-testid="tx-filter-search"
          />
        </div>

        {/* Mobile-only filters toggle */}
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1.5 md:hidden"
          onClick={() => setShowFilters((v) => !v)}
          data-testid="tx-filters-toggle"
        >
          <Filter className="w-3.5 h-3.5" />
          Filters
          {activeFilterCount > 0 && (
            <span className="min-w-[16px] h-4 px-1 rounded-full bg-[#108981] text-white text-[10px] font-bold inline-flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </Button>

        <div className={showFilters ? 'contents' : 'hidden md:contents'}>
        <Input type="date" value={filters?.startDate || ''} onChange={e => setFilter('startDate', e.target.value)} className="h-9 w-[140px] text-xs" data-testid="tx-filter-start" />
        <Input type="date" value={filters?.endDate || ''} onChange={e => setFilter('endDate', e.target.value)} className="h-9 w-[140px] text-xs" data-testid="tx-filter-end" />

        <Select value={filters?.bankId || 'all'} onValueChange={(v) => setFilter('bankId', v)}>
          <SelectTrigger className="h-9 w-[180px] text-xs" data-testid="tx-filter-bank">
            <SelectValue placeholder="All banks" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All banks</SelectItem>
            {bankAccounts.map(b => {
              const last4 = (b.account_number || '').slice(-4);
              const label = `${b.bank_name || 'Bank'}${last4 ? ` ····${last4}` : ''}`;
              return (
                <SelectItem key={b.id} value={b.id}>{label}</SelectItem>
              );
            })}
          </SelectContent>
        </Select>

        <Select value={filters?.glCode || 'all'} onValueChange={(v) => setFilter('glCode', v)}>
          <SelectTrigger className="h-9 w-[160px] text-xs" data-testid="tx-filter-gl">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {glAccounts.slice(0, 60).map(a => (
              <SelectItem key={a.code} value={a.code}>{a.code} · {a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters?.direction || 'all'} onValueChange={(v) => setFilter('direction', v)}>
          <SelectTrigger className="h-9 w-[120px] text-xs" data-testid="tx-filter-direction">
            <SelectValue placeholder="In / Out" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">In + Out</SelectItem>
            <SelectItem value="inflow">Money in</SelectItem>
            <SelectItem value="outflow">Money out</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters?.source || 'all'} onValueChange={(v) => setFilter('source', v)}>
          <SelectTrigger className="h-9 w-[120px] text-xs" data-testid="tx-filter-source">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="statement">Statement upload</SelectItem>
            <SelectItem value="connected">Connected bank</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filters?.confidence || 'all'} onValueChange={(v) => setFilter('confidence', v)}>
          <SelectTrigger className="h-9 w-[120px] text-xs" data-testid="tx-filter-confidence">
            <SelectValue placeholder="AI confidence" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All confidence</SelectItem>
            <SelectItem value="high">High (≥80%)</SelectItem>
            <SelectItem value="med">Medium</SelectItem>
            <SelectItem value="low">Low (&lt;65%)</SelectItem>
          </SelectContent>
        </Select>

        {(taxCtx?.show_vat || taxCtx?.show_wht) && (
          <Select value={filters?.tax || 'all'} onValueChange={(v) => setFilter('tax', v)}>
            <SelectTrigger className="h-9 w-[140px] text-xs" data-testid="tx-filter-tax">
              <SelectValue placeholder="Tax" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {taxCtx?.show_vat && <SelectItem value="vat">Has VAT</SelectItem>}
              {taxCtx?.show_wht && <SelectItem value="wht">Has WHT</SelectItem>}
              <SelectItem value="none">No tax</SelectItem>
            </SelectContent>
          </Select>
        )}

        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-9 px-2 text-xs"
            data-testid="tx-filter-clear"
            onClick={() => onFiltersChange?.({})}
          >
            <X className="w-3.5 h-3.5 mr-1" /> Clear ({activeFilterCount})
          </Button>
        )}
        </div>

        {/* View toggle */}
        {showViewToggle && (
        <div className="ml-auto inline-flex items-center rounded-md border border-border p-0.5 bg-muted/30" data-testid="tx-view-toggle">
          <button
            onClick={() => onViewModeChange?.('table')}
            data-testid="tx-view-table"
            className={`px-2 py-1 rounded text-xs inline-flex items-center gap-1 ${viewMode === 'table' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}
          >
            <TableIcon className="w-3.5 h-3.5" /> Table
          </button>
          <button
            onClick={() => onViewModeChange?.('cards')}
            data-testid="tx-view-cards"
            className={`px-2 py-1 rounded text-xs inline-flex items-center gap-1 ${viewMode === 'cards' ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground'}`}
          >
            <LayoutGrid className="w-3.5 h-3.5" /> Cards
          </button>
        </div>
        )}
      </Card>

      {/* Row 3 - Bulk actions toolbar
          NOTE: removed iter 708. The standalone <BulkActionsToolbar /> is the
          single source of truth (rendered by BankFeedTransactionsTab right
          after this header). Rendering both produced duplicate testids in
          the DOM and ambiguous click targets. */}

      {/* Row 4 - Tax summary chips */}
      {(taxCtx?.show_vat || taxCtx?.show_wht || taxCtx?.show_sales_tax) && (
        <div className="flex flex-wrap items-center gap-2" data-testid="tx-tax-chips">
          {taxCtx?.show_vat && (
            <Chip
              testId="tax-chip-vat"
              active={filters?.tax === 'vat'}
              onClick={() => setFilter('tax', filters?.tax === 'vat' ? 'all' : 'vat')}
              count={vatCandidates}
              tone="border-emerald-300 text-emerald-700 bg-emerald-50"
            >
              <Receipt className="w-3 h-3" />
              {taxCtx.vat_label || 'VAT'}-eligible
            </Chip>
          )}
          {taxCtx?.show_wht && (
            <Chip
              testId="tax-chip-wht"
              active={filters?.tax === 'wht'}
              onClick={() => setFilter('tax', filters?.tax === 'wht' ? 'all' : 'wht')}
              count={whtCandidates}
              tone="border-indigo-300 text-indigo-700 bg-indigo-50"
            >
              <Receipt className="w-3 h-3" />
              WHT-eligible
            </Chip>
          )}
          {potentialInputVat > 0 && (
            <span className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="tax-chip-input-vat">
              <Sparkles className="w-3 h-3" />
              Potential input {taxCtx?.vat_label || 'VAT'}: <b className="text-foreground tabular-nums">{formatCurrency(potentialInputVat, currency)}</b>
            </span>
          )}
          {!taxCtx?.supported && taxCtx?.configure_url && (
            <a href={taxCtx.configure_url} className="text-xs underline text-[#108981] inline-flex items-center gap-1" data-testid="tax-configure-cta">
              Configure tax →
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionTableHeader;
